<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { adjustmentRequest, catalogKey, commonName, formatMicro, priceWatch, type CatalogNote } from "@split/core";
  import { app } from "../lib/store.svelte.ts";
  import { centsInput, formatCents, readMoney, shortDate, today as localToday } from "../lib/format.ts";

  const today = $derived(localToday());
  const notes = $derived(app.viewState.catalogNotes ?? {});
  const list = $derived(priceWatch(app.ledger, notes, today));
  const lower = $derived(list.filter((i) => i.refundMicro > 0));
  const back = $derived(lower.reduce((s, i) => s + i.refundMicro, 0));
  let showAll = $state(false);
  const shown = $derived(showAll ? list : list.slice(0, 12));

  async function setNow(itemNo: string, name: string, text: string) {
    const key = catalogKey({ itemNo, name });
    const cur: CatalogNote = notes[key] ?? { keywords: [] };
    const cents = text.trim() === "" ? null : readMoney(text);
    if (text.trim() !== "" && (cents === null || cents < 0)) return app.toast("Enter a price like 12.99", "error");
    const next: CatalogNote = { ...cur, source: cur.source ?? "you" };
    if (cents === null) {
      delete next.priceNowCents;
      delete next.priceSeenOn;
    } else {
      next.priceNowCents = cents;
      next.priceSeenOn = today;
    }
    await app.do_({ type: "catalog.note", key, note: next }, "Saved");
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(adjustmentRequest(list));
      app.toast("Copied. Show it at the membership counter.");
    } catch {
      app.toast("Couldn't copy here", "error");
    }
  }
</script>

{#if list.length}
  <section class="watch">
    <div class="head">
      <div>
        <h2>Price adjustments</h2>
        {#if H.PRICE_WATCH_NOTE.trim()}<p class="muted small">{H.PRICE_WATCH_NOTE}</p>{/if}
      </div>
      {#if lower.length}
        <div class="sum">
          <strong>{formatMicro(back)}</strong>
          <span class="muted small">to ask for on {lower.length} {lower.length === 1 ? "item" : "items"}</span>
          <button class="btn small primary" onclick={copy}>Copy the list</button>
        </div>
      {/if}
    </div>
    <div class="list">
      <div class="w head" aria-hidden="true"><span>Item</span><span>Paid</span><span>Price now</span><span>Ask by</span></div>
      {#each shown as i (i.itemId)}
        <div class="w" class:hit={i.refundMicro > 0}>
          <span class="what">
            <span><span class="code">#{i.itemNo}</span> {i.name}{#if commonName(i.name, notes[`#${i.itemNo}`])}{" "}<span class="muted">({commonName(i.name, notes[`#${i.itemNo}`])})</span>{/if}</span>
            <span class="muted small">
              Bought {shortDate(i.date)}{#if i.flagged} · <span class="flag">noted for a price adjustment</span>{/if}{#if i.refundMicro > 0} · <strong class="ok">{formatMicro(i.refundMicro)} back</strong>{/if}
            </span>
          </span>
          <span class="num">{formatCents(i.paidCents)}</span>
          <input
            class="input money"
            inputmode="decimal"
            placeholder="—"
            value={i.nowCents === null ? "" : centsInput(i.nowCents)}
            aria-label="Price now for {i.name}"
            disabled={!app.canEdit}
            onchange={(e) => setNow(i.itemNo, i.name, (e.currentTarget as HTMLInputElement).value)}
          />
          <span class="num" class:soon={i.daysLeft <= 5}>{shortDate(i.deadline)}<span class="muted small"> ({i.daysLeft}d)</span></span>
        </div>
      {/each}
    </div>
    {#if list.length > 12}
      <button class="btn small quiet" onclick={() => (showAll = !showAll)}>{showAll ? "Show fewer" : `Show all ${list.length}`}</button>
    {/if}
  </section>
{/if}

<style>
  .watch {
    margin-top: 28px;
  }
  .head {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .head p {
    margin: 4px 0 0;
    max-width: 60ch;
  }
  .sum {
    display: grid;
    justify-items: end;
    gap: 4px;
  }
  .sum strong {
    font-size: 1.25rem;
    color: var(--ok);
  }
  .small {
    font-size: var(--t-xs);
  }
  .w {
    display: grid;
    grid-template-columns: 1fr 80px 96px 110px;
    gap: 12px;
    align-items: center;
    padding: 8px 16px;
    background: var(--surface);
    font-size: var(--t-sm);
  }
  .w.head {
    font-size: var(--t-xs);
    font-weight: 600;
    color: var(--muted);
  }
  .w > :nth-child(n + 2) {
    text-align: right;
  }
  .w .input {
    padding: 4px 8px;
    min-height: 30px;
    font-size: var(--t-xs);
  }
  .w.hit {
    background: color-mix(in srgb, var(--ok) 7%, var(--surface));
  }
  .what {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .ok {
    color: var(--ok);
  }
  .flag {
    color: #b7860b;
  }
  .soon {
    color: var(--owes);
  }
  @media (max-width: 560px) {
    .w {
      grid-template-columns: 1fr 80px 84px;
    }
    .w > :nth-child(4) {
      display: none;
    }
  }
</style>
